import React from 'react';

export interface GutScoreGaugeProps {
  ratio: number;
  leftValue: React.ReactNode;
  leftLabel: string;
  leftSub: string;
  rightValue: React.ReactNode;
  rightLabel: string;
  rightSub: string;
  centerLabel: string;
  /** Optional line under the gauge, e.g. "↓ 9 vs last week · stools mostly type 5". */
  reason?: string;
}

const DELTA_RE = /^([↓↑])\s*(\d+)\s+vs last week/;

function ReasonLine({ reason }: { reason: string }) {
  const m = reason.match(DELTA_RE);
  const delta = m ? m[0] : null;
  const rest = delta ? reason.slice(delta.length) : reason;
  const deltaClass = m?.[1] === '↓' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
  return (
    <p className="pb-1 text-center text-xs font-medium leading-snug text-slate-500 dark:text-zinc-400">
      {delta && <span className={`font-semibold ${deltaClass}`}>{delta}</span>}
      {rest}
    </p>
  );
}

/** The existing half-circle gauge cluster (left metric | gauge | right metric), extracted unchanged from InsightsView. */
export function GutScoreGauge({
  ratio,
  leftValue,
  leftLabel,
  leftSub,
  rightValue,
  rightLabel,
  rightSub,
  centerLabel,
  reason,
}: GutScoreGaugeProps) {
  const gaugeColor = ratio < 30 ? '#EF4444' : ratio < 80 ? '#F59E0B' : '#10B981';
  const badgeStyle =
    ratio < 30
      ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-500/30'
      : ratio < 80
      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30'
      : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30';
  const statusText = ratio < 30 ? 'FLARING' : ratio < 80 ? 'SENSITIVE' : 'STABLE';

  const cluster = (
    <div className="grid grid-cols-3 items-center py-2">
      <div className="text-center flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-slate-950 dark:text-white tracking-tight leading-none">
          {leftValue}
        </span>
        <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider mt-1">
          {leftLabel}
        </span>
        <span className="text-[9px] font-medium text-slate-400 dark:text-zinc-500">{leftSub}</span>
      </div>

      <div className="relative flex items-center justify-center">
        <div className="relative w-40 h-40 flex items-center justify-center">
          <svg viewBox="0 0 160 160" className="w-40 h-40 overflow-visible">
            <circle
              cx="80"
              cy="80"
              r="65"
              fill="none"
              stroke="currentColor"
              className="text-slate-100 dark:text-zinc-800"
              strokeWidth="9.5"
              strokeLinecap="round"
              strokeDasharray="260.9 408.4"
              transform="rotate(155 80 80)"
            />
            <circle
              cx="80"
              cy="80"
              r="65"
              fill="none"
              stroke={gaugeColor}
              strokeWidth="9.5"
              strokeLinecap="round"
              strokeDasharray="260.9 408.4"
              strokeDashoffset={260.93 * (1 - Math.min(100, Math.max(0, ratio)) / 100)}
              transform="rotate(155 80 80)"
              className="transition-all duration-700 ease-out"
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
            <span className="text-[32px] font-black text-slate-950 dark:text-white tracking-tight leading-none">
              {ratio}%
            </span>
            <span className="text-[9.5px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider mt-1">
              {centerLabel}
            </span>
            <span className={`mt-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-black ${badgeStyle}`}>
              {statusText}
            </span>
          </div>
        </div>
      </div>

      <div className="text-center flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-slate-950 dark:text-white tracking-tight leading-none">
          {rightValue}
        </span>
        <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider mt-1">
          {rightLabel}
        </span>
        <span className="text-[9px] font-medium text-slate-400 dark:text-zinc-500">{rightSub}</span>
      </div>
    </div>
  );

  if (!reason) return cluster;
  return (
    <>
      {cluster}
      <ReasonLine reason={reason} />
    </>
  );
}
